<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

function formatCurrency(value) {
  if (!value) return 'R$ 0';
  return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
}

function formatNumber(num) {
  if (!num) return '0';
  return num.toLocaleString('pt-BR');
}

const winRate = computed(() => {
  const rate = props.data?.win_rate || 0;
  return `${rate.toFixed(1)}%`;
});

const avgDealCycle = computed(() => {
  const days = props.data?.avg_deal_cycle_days || 0;
  return `${days.toFixed(0)} dias`;
});
</script>

<template>
  <div class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center gap-2 mb-4">
      <span class="i-lucide-briefcase text-lg text-n-blue-10" />
      <h3 class="text-base font-semibold text-n-slate-12">Resumo do CRM</h3>
    </div>

    <div v-if="loading" class="flex items-center justify-center h-32">
      <Spinner size="large" />
    </div>

    <div v-else class="grid grid-cols-2 md:grid-cols-4 gap-4">
      <!-- Funnels -->
      <div class="flex flex-col p-3 rounded-lg bg-n-alpha-1">
        <span class="text-xs text-n-slate-10 mb-1">Funis</span>
        <span class="text-2xl font-bold text-n-slate-12">{{
          formatNumber(data.total_funnels)
        }}</span>
        <span class="text-xs text-n-slate-11">{{ formatNumber(data.total_stages) }} etapas</span>
      </div>

      <!-- Deals -->
      <div class="flex flex-col p-3 rounded-lg bg-n-alpha-1">
        <span class="text-xs text-n-slate-10 mb-1">Negocios</span>
        <span class="text-2xl font-bold text-n-slate-12">{{
          formatNumber(data.total_deals)
        }}</span>
        <div class="flex gap-2 text-xs">
          <span class="text-n-teal-11">{{ formatNumber(data.deals_won) }} ganhos</span>
          <span class="text-n-ruby-11">{{ formatNumber(data.deals_lost) }} perdidos</span>
        </div>
      </div>

      <!-- Pipeline Value -->
      <div class="flex flex-col p-3 rounded-lg bg-n-alpha-1">
        <span class="text-xs text-n-slate-10 mb-1">Pipeline</span>
        <span class="text-xl font-bold text-n-blue-11">{{
          formatCurrency(data.total_pipeline_value)
        }}</span>
        <span class="text-xs text-n-slate-11">{{ formatNumber(data.deals_open) }} em aberto</span>
      </div>

      <!-- Won Value -->
      <div class="flex flex-col p-3 rounded-lg bg-n-alpha-1">
        <span class="text-xs text-n-slate-10 mb-1">Valor Ganho</span>
        <span class="text-xl font-bold text-n-teal-11">{{
          formatCurrency(data.total_won_value)
        }}</span>
        <span class="text-xs text-n-slate-11">{{ winRate }} taxa de conversao</span>
      </div>

      <!-- Avg Deal Value -->
      <div class="flex flex-col p-3 rounded-lg bg-n-alpha-1">
        <span class="text-xs text-n-slate-10 mb-1">Ticket Medio</span>
        <span class="text-xl font-bold text-n-violet-11">{{
          formatCurrency(data.avg_deal_value)
        }}</span>
      </div>

      <!-- Deal Cycle -->
      <div class="flex flex-col p-3 rounded-lg bg-n-alpha-1">
        <span class="text-xs text-n-slate-10 mb-1">Ciclo Medio</span>
        <span class="text-xl font-bold text-n-amber-11">{{
          avgDealCycle
        }}</span>
      </div>

      <!-- Products -->
      <div class="flex flex-col p-3 rounded-lg bg-n-alpha-1">
        <span class="text-xs text-n-slate-10 mb-1">Produtos</span>
        <span class="text-2xl font-bold text-n-slate-12">{{
          formatNumber(data.total_products)
        }}</span>
        <span class="text-xs text-n-slate-11">{{
          formatCurrency(data.products_value)
        }}</span>
      </div>

      <!-- Appointments -->
      <div class="flex flex-col p-3 rounded-lg bg-n-alpha-1">
        <span class="text-xs text-n-slate-10 mb-1">Agendamentos</span>
        <span class="text-2xl font-bold text-n-slate-12">{{
          formatNumber(data.total_appointments)
        }}</span>
        <span class="text-xs text-n-amber-11">{{ formatNumber(data.appointments_today) }} hoje</span>
      </div>
    </div>
  </div>
</template>
